import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Rocket, Wand2 } from "lucide-react";
import { api, useAuth } from "../lib/api";
import { Badge, Button, Card, Mono, PageHeader, Skeleton, Table } from "../components/ui";
import { EstimatedBadge, MeasuredBadge } from "../components/ui/shared";
import { canDeploy } from "./Models";
import { FIT_TONE, UC_ICONS, fitMark } from "./UseCases";

const AVAIL = { always_on: "Always on (spare copy)", standard: "Standard" };

export default function UseCaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [u, setU] = useState(null);
  const [compat, setCompat] = useState(null);
  useEffect(() => { api(`/use-cases/${id}`).then(setU).catch((e) => { toast.error(e.message); nav("/use-cases"); }); api("/models/compat").then(setCompat).catch(() => setCompat({ models: {} })); }, [id]);
  if (!u) return <Skeleton className="h-64" />;
  const Icon = UC_ICONS[u.icon] || Wand2;
  const w = u.workload;
  const write = canDeploy(user);
  return (<div>
    <Link to="/use-cases" className="text-xs text-cx-muted inline-flex items-center gap-1 mb-2 hover:text-cx-emerald"><ArrowLeft size={12} /> Use cases</Link>
    <PageHeader title={<span className="inline-flex items-center gap-3"><Icon size={22} className="text-cx-emerald" />{u.name}</span>} subtitle={u.description}
      actions={write && <Button onClick={() => nav(`/deployments/new?use_case=${u.id}`)} data-testid="use-case-deploy-cta"><Rocket size={14} /> Deploy for this use case</Button>} />
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <h3 className="cx-card-title mb-3">Recommended models</h3>
          <Table head={["Model", "Size", "Fits your infrastructure?", "Speed", "Notes"]} testid="use-case-models-table">
            {u.suggested_models.map((m, i) => {
              const c = m.model_id ? compat?.models?.[m.model_id] : null;
              return (<tr key={m.repo_id} data-testid={`uc-model-row-${m.name}`}>
                <td>{m.model_id ? <Link className="font-medium hover:text-cx-emerald" to={`/models/${m.model_id}`}>{m.name}</Link> : <span className="font-medium">{m.name}</span>}{i === 0 && <Badge color="green" className="ml-2">Recommended</Badge>}{m.gated && <Badge color="yellow" className="ml-1">gated</Badge>}</td>
                <td>{m.params_label || "—"}</td>
                <td>{m.fit ? <Badge color={FIT_TONE[m.fit.status]} title={m.fit.label}>{fitMark(m.fit)}</Badge> : m.in_catalog ? <Badge color="gray">checking…</Badge> : <Badge color="gray">not in catalogue</Badge>}</td>
                <td>{c?.measured ? <>~{c.measured.words_s} words/s <MeasuredBadge /></> : <>{m.fit?.status === "fits" ? "estimated by the planner" : "—"} {m.fit?.status === "fits" && <EstimatedBadge />}</>}</td>
                <td className="text-xs text-cx-muted">{m.fit?.label || (m.in_catalog ? "" : "Add from Hugging Face in Models")}{m.fit?.status === "fits" ? " · cost shown in the deploy wizard" : ""}</td>
              </tr>);
            })}
          </Table>
        </Card>
        <Card>
          <h3 className="cx-card-title mb-3">Example exchange</h3>
          {u.sample_requests.map((s, i) => <div key={i} className="grid md:grid-cols-2 gap-3 text-xs" data-testid="use-case-sample">
            <div><div className="cx-stat-label mb-1">Input</div><pre className="cx-mono whitespace-pre-wrap p-3 rounded-lg bg-black/30 border border-cx-border">{s.input}</pre></div>
            <div><div className="cx-stat-label mb-1">Output</div><pre className="cx-mono whitespace-pre-wrap p-3 rounded-lg bg-black/30 border border-cx-border">{s.output}</pre></div>
          </div>)}
          <div className="text-xs text-cx-muted mt-3">System prompt: <Mono>{u.system_prompt}</Mono></div>
        </Card>
      </div>
      <div className="space-y-6">
        <Card>
          <h3 className="cx-card-title mb-2">Why run it privately</h3>
          <p className="text-sm">{u.why_private}</p>
        </Card>
        <Card>
          <h3 className="cx-card-title mb-3">Workload profile</h3>
          <dl className="text-sm space-y-1.5" data-testid="use-case-workload">
            <div className="flex justify-between"><dt className="text-cx-muted">People or systems</dt><dd>{w.users}</dd></div>
            <div className="flex justify-between"><dt className="text-cx-muted">At the same time</dt><dd>~{w.concurrency}</dd></div>
            <div className="flex justify-between"><dt className="text-cx-muted">Input length</dt><dd>~{w.prompt_tokens} tokens</dd></div>
            <div className="flex justify-between"><dt className="text-cx-muted">Answer length</dt><dd>~{w.answer_tokens} tokens</dd></div>
            <div className="flex justify-between"><dt className="text-cx-muted">First word within</dt><dd>{w.first_word_s} s</dd></div>
            <div className="flex justify-between"><dt className="text-cx-muted">Availability</dt><dd>{AVAIL[w.availability] || w.availability}</dd></div>
          </dl>
          <div className="flex flex-wrap gap-1 mt-3">{u.chips.map((c) => <Badge key={c} color="gray">{c}</Badge>)}</div>
        </Card>
        <Card>
          <h3 className="cx-card-title mb-2">Endpoints serving this use case</h3>
          {u.live_endpoints.length === 0 ? <div className="text-sm text-cx-muted" data-testid="use-case-no-endpoints">None yet.</div>
            : <ul className="text-sm space-y-1" data-testid="use-case-endpoints">{u.live_endpoints.map((e) => <li key={e}><Link className="hover:text-cx-emerald font-medium" to={`/endpoints/${e}`}>{e}</Link></li>)}</ul>}
          {u.live_deployments.length > 0 && <div className="text-xs text-cx-muted mt-2">Deployments: {u.live_deployments.map((d) => <Link key={d.id} className="underline mr-2" to={`/deployments/${d.id}`}>{d.name}</Link>)}</div>}
        </Card>
        <Card>
          <h3 className="cx-card-title mb-2">Default endpoint limits</h3>
          <div className="text-xs text-cx-muted">{u.default_limits.rpm} requests/min · {(u.default_limits.tokens_per_day / 1e6).toFixed(0)}M tokens/day · answers up to {u.default_limits.max_tokens} tokens · {u.default_access.limits_preset} preset</div>
        </Card>
      </div>
    </div>
  </div>);
}
